import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Plus, Save, UserX } from "lucide-react";

import {
  type AdminUser,
  type Language,
  adminUsersApi,
} from "@/api/adminUsers";
import type { Role } from "@/api/auth";
import { useMe } from "@/hooks/useMe";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const ROLES: Role[] = ["manager", "admin", "super_admin"];
const LANGS: Language[] = ["uk", "ru"];

function fmtDateTime(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("uk-UA", { timeZone: "Europe/Kyiv" });
}

export function AdminUsersPage() {
  const { t } = useTranslation();
  const me = useMe();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);

  const isSuperAdmin = me.data?.role === "super_admin";

  const users = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => adminUsersApi.listUsers(),
    enabled: isSuperAdmin,
  });

  const tenants = useQuery({
    queryKey: ["admin-tenants"],
    queryFn: () => adminUsersApi.listTenants(),
    enabled: isSuperAdmin,
  });

  const tenantsById = new Map((tenants.data ?? []).map((tn) => [tn.id, tn.name]));

  const deactivate = useMutation({
    mutationFn: (id: string) => adminUsersApi.deactivateUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  if (!isSuperAdmin) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500">{t("common.read_only")}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("admin_users.title")}</CardTitle>
              <CardDescription>{t("admin_users.subtitle")}</CardDescription>
            </div>
            <Button onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" /> {t("admin_users.add")}
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {users.isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : (users.data ?? []).length === 0 ? (
            <div className="p-6 text-sm text-slate-500">{t("admin_users.no_users")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="p-2">{t("admin_users.email")}</th>
                    <th className="p-2">{t("admin_users.full_name")}</th>
                    <th className="p-2">{t("admin_users.role")}</th>
                    <th className="p-2">{t("admin_users.tenants")}</th>
                    <th className="p-2">{t("admin_users.last_login")}</th>
                    <th className="p-2">{t("common.active")}</th>
                    <th className="p-2 text-right">{t("periods.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.data!.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100">
                      <td className="p-2 font-mono text-xs">
                        <button onClick={() => setEditing(u)} className="hover:underline">
                          {u.email}
                        </button>
                      </td>
                      <td className="p-2">{u.full_name}</td>
                      <td className="p-2">
                        <span className="font-mono text-xs">{u.role}</span>
                      </td>
                      <td className="p-2 text-xs text-slate-500">
                        {u.tenant_ids.map((tid) => tenantsById.get(tid) ?? tid.slice(0, 8)).join(", ") || "—"}
                      </td>
                      <td className="p-2 text-xs text-slate-500">{fmtDateTime(u.last_login_at)}</td>
                      <td className="p-2">{u.is_active ? "✓" : "—"}</td>
                      <td className="p-2 text-right">
                        {u.is_active && u.id !== me.data?.id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (confirm(t("admin_users.deactivate_confirm", { email: u.email }))) {
                                deactivate.mutate(u.id);
                              }
                            }}
                          >
                            <UserX className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {(adding || editing) && (
        <UserFormModal
          user={editing}
          allTenants={tenants.data ?? []}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => {
            setAdding(false);
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ["admin-users"] });
          }}
        />
      )}
    </div>
  );
}

function UserFormModal({
  user,
  allTenants,
  onClose,
  onSaved,
}: {
  user: AdminUser | null;
  allTenants: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: user?.email ?? "",
    full_name: user?.full_name ?? "",
    role: user?.role ?? ("manager" as Role),
    language: user?.language ?? ("uk" as Language),
    password: "",
    new_password: "",
    is_active: user?.is_active ?? true,
    tenant_ids: new Set<string>(user?.tenant_ids ?? []),
  });
  const [error, setError] = useState<string | null>(null);

  const toggleTenant = (id: string) => {
    setForm((p) => {
      const next = new Set(p.tenant_ids);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...p, tenant_ids: next };
    });
  };

  const save = useMutation({
    mutationFn: () => {
      if (user) {
        const patch = {
          full_name: form.full_name,
          role: form.role,
          language: form.language,
          is_active: form.is_active,
          tenant_ids: Array.from(form.tenant_ids),
          ...(form.new_password ? { new_password: form.new_password } : {}),
        };
        return adminUsersApi.updateUser(user.id, patch);
      }
      return adminUsersApi.createUser({
        email: form.email,
        full_name: form.full_name,
        role: form.role,
        language: form.language,
        password: form.password,
        is_active: form.is_active,
        tenant_ids: Array.from(form.tenant_ids),
      });
    },
    onSuccess: () => onSaved(),
    onError: (e: unknown) => {
      const code = (e as AxiosError<{ error?: { code?: string } }>)?.response?.data?.error?.code;
      setError(code === "email_already_exists" ? t("admin_users.errors.email_taken") : code ?? t("clients.errors.unknown"));
    },
  });

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>{user ? t("admin_users.edit") : t("admin_users.add")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{t("admin_users.email")}</Label>
            <Input
              type="email"
              disabled={!!user}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>{t("admin_users.full_name")}</Label>
            <Input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t("admin_users.role")}</Label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t("admin_users.language")}</Label>
              <select
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value as Language })}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                {LANGS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!user && (
            <div className="space-y-1">
              <Label>{t("admin_users.password")}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
          )}
          {user && (
            <div className="space-y-1">
              <Label>{t("admin_users.reset_password")}</Label>
              <Input
                type="password"
                placeholder={t("sources.password_keep_hint")}
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              />
              <p className="text-xs text-slate-500">{t("admin_users.reset_password_hint")}</p>
            </div>
          )}

          <div className="space-y-1">
            <Label>{t("admin_users.tenants")}</Label>
            <div className="rounded-md border border-slate-200 p-2 space-y-1 max-h-40 overflow-y-auto">
              {allTenants.length === 0 ? (
                <p className="text-xs text-slate-500">{t("admin_tenants.no_tenants")}</p>
              ) : (
                allTenants.map((tn) => (
                  <label key={tn.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.tenant_ids.has(tn.id)}
                      onChange={() => toggleTenant(tn.id)}
                    />
                    {tn.name}
                  </label>
                ))
              )}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            {t("common.active")}
          </label>

          {error && <div className="text-sm text-red-600">{error}</div>}
        </CardContent>
        <div className="flex gap-2 px-6 pb-6">
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || !form.email || !form.full_name || (!user && !form.password)}
          >
            <Save className="h-4 w-4" />
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
